import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { PlusIcon } from '@radix-ui/react-icons'
import { useScatterplotContext } from 'features/timeSeries'
import {
  DismissButton,
  formatOverlayTimestamp,
  overlayPanelSurface,
} from 'features/timeSeries/components/chartOverlays/overlayChrome'
import { SensorSelect } from 'features/timeSeries/components/chartTools/SensorSelect'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { RulerMetrics, RulerSelection, VisibleSensorInfo } from './useRulerTool'

/* ─── Styled components ─────────────────────────────────────────────────── */

const Panel = styled.div<{ $isMinimized?: boolean }>`
  ${overlayPanelSurface}
  padding: ${({ $isMinimized }) => ($isMinimized ? '4px 6px' : '7px 10px')};
  min-width: ${({ $isMinimized }) => ($isMinimized ? '0' : '295px')};
  width: ${({ $isMinimized }) => ($isMinimized ? 'auto' : 'fit-content')};
  max-width: ${({ $isMinimized }) => ($isMinimized ? 'none' : '600px')};
  overflow-x: ${({ $isMinimized }) => ($isMinimized ? 'visible' : 'auto')};
  overflow-y: hidden;
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
  color: #ccc;
`

const PanelHeader = styled.div<{ $isDragging?: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 4px;
  margin-bottom: 5px;
  cursor: ${({ $isDragging }) => ($isDragging ? 'grabbing' : 'grab')};
`

const TimeRangeRow = styled.div`
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 9px;
  /* Deliberately brighter than CHART_COLORS.TOOLTIP_TIME_COLOR: this is the
     panel's primary content, not a transient tooltip, and it renders at 9px.
     Stays below the #fff metric values so the hierarchy holds. */
  color: #ccc;
  line-height: 1.4;
`

const CloseButton = styled(DismissButton)`
  margin-left: 6px;
`

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const MinimizeButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  line-height: 1;
  font-size: 13px;
  flex-shrink: 0;
  margin-left: 6px;
  width: 12px;
  text-align: center;

  &:hover {
    color: #fff;
  }
`

/* ─── Pen row ─────────────────────────────────────────────────────────── */

const PensRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 5px;
  margin-bottom: 6px;
`

const ColorDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

/* Additional sensors — chip with name and remove button */
const SensorChip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 3px;
  padding: 2px 4px 2px 5px;
  max-width: 150px;
`

const ChipName = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
`

const RemoveChipButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0 0 0 2px;
  /* Measured against the chip's lighter background, not the panel's. */
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  font-size: 11px;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    color: #fff;
  }
`

/* Add sensor (+) button */
const AddButton = styled(DropdownMenu.Trigger)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: none;
  border-radius: 3px;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  outline: none;
  padding: 2px 5px;
  font-size: 13px;
  line-height: 1;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
  }
`

/* Shared dropdown content */
const SensorContent = styled(DropdownMenu.Content)`
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  padding: 3px;
  z-index: 99999;
  min-width: 180px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
`

const SensorItem = styled(DropdownMenu.Item)<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
  font-weight: ${({ $isSelected }) => ($isSelected ? 600 : 400)};
  color: #fff;
  padding: 4px 8px;
  border-radius: 3px;
  cursor: pointer;
  outline: none;

  &[data-highlighted] {
    background: rgba(255, 255, 255, 0.1);
  }
`

/* ─── Metrics grid ────────────────────────────────────────────────────── */

const MetricsGrid = styled.div<{ $template: string }>`
  display: grid;
  grid-template-columns: ${({ $template }) => $template};
  gap: 3px 8px;
  align-items: baseline;
`

const MetricLabel = styled.span`
  color: ${CHART_COLORS.TOOLTIP_NAME_COLOR};
  white-space: nowrap;
  font-size: 11px;
`

/* Single-sensor timestamp */
const MetricTime = styled.span`
  color: ${CHART_COLORS.TOOLTIP_TIME_COLOR};
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 10px;
  white-space: nowrap;
`

const MetricValue = styled.span`
  color: ${CHART_COLORS.TOOLTIP_VALUE_COLOR};
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-variant-numeric: tabular-nums;
  font-weight: 400;
  font-size: 11px;
  text-align: right;
  white-space: nowrap;
`

const NoDataMessage = styled.div`
  color: #555;
  font-style: italic;
  text-align: center;
  padding: 3px 0;
`

/* ─── Helpers ─────────────────────────────────────────────────────────── */

function formatValue(v: number | null, unit?: string): string {
  if (v === null) return '—'
  const formatted =
    Math.abs(v) >= 1000
      ? v.toLocaleString(undefined, { maximumFractionDigits: 2 })
      : v.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 4,
        })
  return unit ? `${formatted} ${unit}` : formatted
}

/* Nullable metrics (an empty selection has no max/min time) are a ruler-only
   concern, so the null handling stays local to the shared formatter. */
function formatTimestamp(ts: number | null): string {
  if (ts === null) return ''
  return formatOverlayTimestamp(ts)
}

/* ─── Props ───────────────────────────────────────────────────────────── */

interface RulerMetricsPanelProps {
  selection: RulerSelection
  visibleSensors: VisibleSensorInfo[]
  selectedSensorIds: string[]
  onSetSensorIds: (ids: string[]) => void
  onDismiss: () => void
  onHeaderMouseDown?: React.MouseEventHandler<HTMLDivElement>
  isDragging?: boolean
  isMinimized?: boolean
  onToggleMinimized?: () => void
  maxWidth?: number
  computeMetrics: (
    sensorId: string,
    startXValue: number,
    endXValue: number
  ) => RulerMetrics
}

/* ─── Component ───────────────────────────────────────────────────────── */

export const RulerMetricsPanel: React.FC<RulerMetricsPanelProps> = ({
  selection,
  visibleSensors,
  selectedSensorIds,
  onSetSensorIds,
  onDismiss,
  onHeaderMouseDown,
  isDragging = false,
  isMinimized = false,
  onToggleMinimized,
  maxWidth,
  computeMetrics,
}) => {
  const { setHighlightedSensorId } = useScatterplotContext()

  const allMetrics = useMemo(
    () =>
      selectedSensorIds.map((id) =>
        computeMetrics(id, selection.startXValue, selection.endXValue)
      ),
    [selectedSensorIds, selection, computeMetrics]
  )

  const primarySensor = visibleSensors.find(
    (s) => s.sensorId === selectedSensorIds[0]
  )

  const unselectedSensors = visibleSensors.filter(
    (s) => !selectedSensorIds.includes(s.sensorId)
  )

  const isMulti = selectedSensorIds.length > 1
  const hasAnyData = allMetrics.some((m) => m.count > 0)

  const stopProp = (e: React.MouseEvent) => e.stopPropagation()
  const handleSensorMouseEnter = useCallback(
    (sensorId: string) => {
      setHighlightedSensorId(sensorId)
    },
    [setHighlightedSensorId]
  )
  const handleSensorMouseLeave = useCallback(() => {
    setHighlightedSensorId(null)
  }, [setHighlightedSensorId])
  const handleHeaderMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.button !== 0) return

      const eventTarget =
        event.target instanceof HTMLElement ? event.target : null
      if (eventTarget?.closest('button, [role="menuitem"]')) return

      onHeaderMouseDown?.(event)
    },
    [onHeaderMouseDown]
  )

  return (
    <Panel
      $isMinimized={isMinimized}
      style={maxWidth !== undefined && !isMinimized ? { maxWidth } : undefined}>
      {/* Header: time range + minimize + dismiss */}
      <PanelHeader
        data-testid='ruler-metrics-panel-header'
        onMouseDown={handleHeaderMouseDown}
        $isDragging={isDragging}
        style={isMinimized ? { marginBottom: 0, gap: 2 } : undefined}>
        {!isMinimized && (
          <TimeRangeRow>
            {formatTimestamp(selection.startXValue)}
            {' \u2014 '}
            {formatTimestamp(selection.endXValue)}
          </TimeRangeRow>
        )}
        <HeaderControls>
          {onToggleMinimized && (
            <ChartTooltip
              label={isMinimized ? 'Expand' : 'Minimize'}
              side='top'>
              <MinimizeButton
                onMouseDown={stopProp}
                onClick={onToggleMinimized}>
                {isMinimized ? '+' : '–'}
              </MinimizeButton>
            </ChartTooltip>
          )}
          <ChartTooltip label='Dismiss' side='top'>
            <CloseButton onMouseDown={stopProp} onClick={onDismiss}>
              ×
            </CloseButton>
          </ChartTooltip>
        </HeaderControls>
      </PanelHeader>

      {!isMinimized && (
        <>
          {/* Pen selector row */}
          <PensRow onMouseDown={stopProp}>
            {/* Primary sensor — swappable dropdown */}
            {primarySensor && (
              <SensorSelect
                options={visibleSensors}
                selectedSensorId={primarySensor.sensorId}
                onSelect={(sensorId) =>
                  onSetSensorIds([sensorId, ...selectedSensorIds.slice(1)])
                }
                onMouseDown={stopProp}
                maxWidth={150}
              />
            )}

            {/* Additional sensors — chips with × */}
            {selectedSensorIds.slice(1).map((id) => {
              const sensor = visibleSensors.find((s) => s.sensorId === id)
              if (!sensor) return null
              return (
                <SensorChip
                  key={id}
                  onMouseEnter={() => handleSensorMouseEnter(sensor.sensorId)}
                  onMouseLeave={handleSensorMouseLeave}>
                  <ColorDot $color={sensor.color} />
                  <ChipName>{sensor.name}</ChipName>
                  <ChartTooltip label={`Remove ${sensor.name}`} side='top'>
                    <RemoveChipButton
                      onMouseDown={stopProp}
                      onClick={() =>
                        onSetSensorIds(
                          selectedSensorIds.filter((sid) => sid !== id)
                        )
                      }>
                      ×
                    </RemoveChipButton>
                  </ChartTooltip>
                </SensorChip>
              )
            })}

            {/* + button to add more sensors */}
            {unselectedSensors.length > 0 && (
              <DropdownMenu.Root
                onOpenChange={(open) => {
                  if (!open) handleSensorMouseLeave()
                }}>
                <ChartTooltip label='Add pen' side='top'>
                  <AddButton>
                    <PlusIcon />
                  </AddButton>
                </ChartTooltip>
                <DropdownMenu.Portal>
                  <SensorContent
                    sideOffset={4}
                    align='start'
                    onMouseDown={stopProp}
                    onMouseLeave={handleSensorMouseLeave}>
                    {unselectedSensors.map((s) => (
                      <SensorItem
                        key={s.sensorId}
                        onMouseEnter={() => handleSensorMouseEnter(s.sensorId)}
                        onMouseLeave={handleSensorMouseLeave}
                        onSelect={() => {
                          onSetSensorIds([...selectedSensorIds, s.sensorId])
                          handleSensorMouseLeave()
                        }}>
                        <ColorDot $color={s.color} />
                        {s.name}
                      </SensorItem>
                    ))}
                  </SensorContent>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            )}
          </PensRow>

          {/* Metrics */}
          {hasAnyData ? (
            <MetricsGrid
              $template={
                isMulti
                  ? `auto repeat(${selectedSensorIds.length}, minmax(70px, 1fr))`
                  : 'auto 1fr auto'
              }>
              {/* High */}
              <MetricLabel>High</MetricLabel>
              {isMulti ? (
                allMetrics.map((m, i) => (
                  <MetricValue key={i}>
                    {formatValue(m.max, m.unit)}
                  </MetricValue>
                ))
              ) : (
                <>
                  <MetricTime>
                    {formatTimestamp(allMetrics[0]?.maxTime ?? null)}
                  </MetricTime>
                  <MetricValue>
                    {formatValue(
                      allMetrics[0]?.max ?? null,
                      allMetrics[0]?.unit
                    )}
                  </MetricValue>
                </>
              )}

              {/* Low */}
              <MetricLabel>Low</MetricLabel>
              {isMulti ? (
                allMetrics.map((m, i) => (
                  <MetricValue key={i}>
                    {formatValue(m.min, m.unit)}
                  </MetricValue>
                ))
              ) : (
                <>
                  <MetricTime>
                    {formatTimestamp(allMetrics[0]?.minTime ?? null)}
                  </MetricTime>
                  <MetricValue>
                    {formatValue(
                      allMetrics[0]?.min ?? null,
                      allMetrics[0]?.unit
                    )}
                  </MetricValue>
                </>
              )}

              {/* Range */}
              <MetricLabel
                style={isMulti ? undefined : { gridColumn: '1 / 3' }}>
                Range
              </MetricLabel>
              {allMetrics.map((m, i) => (
                <MetricValue key={i}>{formatValue(m.diff, m.unit)}</MetricValue>
              ))}

              {/* Mean */}
              <MetricLabel
                style={isMulti ? undefined : { gridColumn: '1 / 3' }}>
                Mean
              </MetricLabel>
              {allMetrics.map((m, i) => (
                <MetricValue key={i}>{formatValue(m.mean, m.unit)}</MetricValue>
              ))}
            </MetricsGrid>
          ) : (
            <NoDataMessage>
              {selectedSensorIds.length === 0
                ? 'Select a pen'
                : 'No data in selection'}
            </NoDataMessage>
          )}
        </>
      )}
    </Panel>
  )
}
